/**
 * The panel's own login (0.4.0, ADR 0012, specs/c2-kontratlar.md 2.2): username and
 * password, then a 6 digit TOTP code (or a one-time recovery code), then an opaque session
 * cookie. The session gives exactly the principal the Tailscale identity gives the same
 * human. Error bodies stay generic (no user enumeration): a wrong username, a wrong
 * password and a wrong code all read invalid_credentials.
 *
 *   GET  /api/v1/auth/session     public: who is signed in (never 401)
 *   POST /api/v1/auth/login       public: { username, password } -> TOTP challenge or session
 *   POST /api/v1/auth/login/totp  public: { challenge, code | recoveryCode } -> session
 *   POST /api/v1/auth/logout      panel: ends the session, clears the cookie
 */
import { z } from 'zod';
import { isoDateTimeSchema } from '../ids.js';
/** The session cookie: __Host- (Secure, Path=/, no Domain), HttpOnly, SameSite=Strict. */
export const SESSION_COOKIE_NAME = '__Host-twin_session';
/** An opaque session token: 32 random bytes, base64url. Only its sha256 is stored. */
export const SESSION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** Idle and absolute session limits (defaults; TWIN_SESSION_IDLE_HOURS, TWIN_SESSION_MAX_DAYS). */
export const SESSION_IDLE_HOURS_DEFAULT = 12;
export const SESSION_MAX_DAYS_DEFAULT = 7;
/** The TOTP step after a correct password must be finished within this time. */
export const LOGIN_CHALLENGE_TTL_MS = 5 * 60_000;
/** Two base64url parts: claims and HMAC (like the hold token). */
export const LOGIN_CHALLENGE_PATTERN = /^[A-Za-z0-9_-]{16,1024}\.[A-Za-z0-9_-]{43}$/;
export const loginRequestSchema = z.strictObject({
    username: z.string().min(1).max(64),
    /** Refused above 1024 characters before any hashing. */
    password: z.string().min(1).max(1024),
});
export const loginTotpRequestSchema = z
    .strictObject({
    challenge: z.string().regex(LOGIN_CHALLENGE_PATTERN),
    code: z.string().max(16).optional(),
    recoveryCode: z.string().max(32).optional(),
})
    .refine((body) => (body.code === undefined) !== (body.recoveryCode === undefined), {
    error: 'exactly one of code and recoveryCode',
});
export const authViaSchema = z.enum(['session', 'tailscale', 'dev']);
/** GET /auth/session and the end of a successful login. */
export const authSessionSchema = z.object({
    authenticated: z.boolean(),
    via: authViaSchema.nullable(),
    human: z
        .object({
        humanId: z.string().min(1),
        displayName: z.string().min(1),
        role: z.enum(['owner', 'meeting_lead', 'engineer']),
    })
        .nullable(),
    /** The login name (session only). */
    username: z.string().nullable(),
    /** Absolute end of the session (session only). */
    expiresAt: isoDateTimeSchema.nullable(),
    /** End if nothing happens until then (session only). */
    idleExpiresAt: isoDateTimeSchema.nullable(),
});
/** POST /auth/login: the TOTP step next, or (TOTP turned off for this user) the session. */
export const loginResponseSchema = z.discriminatedUnion('next', [
    z.object({
        next: z.literal('totp'),
        challenge: z.string().regex(LOGIN_CHALLENGE_PATTERN),
        expiresAt: isoDateTimeSchema,
    }),
    z.object({ next: z.literal('done'), session: authSessionSchema }),
]);
/** POST /auth/login/totp. */
export const loginTotpResponseSchema = z.object({
    session: authSessionSchema,
    /** Set when a recovery code was used: how many are left. */
    recoveryCodesLeft: z.number().int().nonnegative().nullable(),
});
export const logoutResponseSchema = z.object({ ok: z.literal(true) });
/** Claims of the login challenge (HMAC with a TWIN_AUTH_KEY subkey; API internal, listed for review). */
export const loginChallengeClaimsSchema = z.strictObject({
    v: z.literal(1),
    /** auth.panel_users.user_id */
    uid: z.string().regex(/^[1-9]\d{0,17}$/),
    /** password_changed_at in epoch ms: a password change voids open challenges. */
    pwv: z.number().int().nonnegative(),
    iat: z.number().int().nonnegative(),
    exp: z.number().int().nonnegative(),
    jti: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
});
//# sourceMappingURL=auth.js.map